import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaThumbsUp, FaExclamationTriangle, FaQuestionCircle } from 'react-icons/fa';
import { FaBrain } from 'react-icons/fa6';
import {
  buildSuspensionSeries,
  detectSuspensionEvents,
  computeSuspensionMetrics,
} from '../utils/suspensionAnalysis';
import { runJackPackingAnalysisFromTelemetry } from '../utils/jackPackingEngine';
import { getJackParamsForAnalysis, getTuningGuideConfig } from '../utils/jackSettings';
import { analyzeTuningGuide, buildOpenAiPayload, jackRowKeyForLabel } from '../utils/tuningKbAnalysis';
import { fetchOpenAiTuningInsight, resolveOpenAiApiKey, formatOpenAiInsightHtml } from '../utils/openaiTuningInsight';
import {
  buildSummarySnapshot,
  SUMMARY_DEFAULT_TRAVEL_FRONT_MM,
  SUMMARY_DEFAULT_TRAVEL_REAR_MM,
} from '../utils/suspensionSummarySnapshot';
import { computeRoughnessFromTelemetry } from '../utils/roughnessScore';
import { fetchRoughnessCalibration, getRoughnessCalibrationSync } from '../utils/roughnessCalibration';
import TimeSeriesChart from './TimeSeriesChart';
import JackAdvancedHistograms from './JackAdvancedHistograms';
import JackStrokeBalanceChart from './JackStrokeBalanceChart';
import FrequencyHistogramPanel from './FrequencyHistogramPanel';
import './SuspensionAnalysisPanel.css';

function formatNum(v, digits = 1) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

function formatTime(s) {
  if (s == null || !Number.isFinite(s)) return '—';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(2);
  return m > 0 ? `${m}:${sec.padStart(5, '0')}` : `${sec}s`;
}

function formatDuration(s) {
  if (s == null || !Number.isFinite(s)) return '—';
  if (s < 1) return `${(s * 1000).toFixed(0)} ms`;
  return `${s.toFixed(2)} s`;
}

const MAX_EMBEDDED_CHART_POINTS = 2400;
const MAX_FREQUENCY_INPUT_POINTS = 12000;

function downsampleRows(rows, maxSamples = MAX_EMBEDDED_CHART_POINTS) {
  if (!Array.isArray(rows) || rows.length <= maxSamples) return rows || [];
  const step = Math.ceil(rows.length / maxSamples);
  const sampled = [];
  for (let i = 0; i < rows.length; i += step) sampled.push(rows[i]);
  return sampled;
}

function downsampleSuspensionSeries(series, maxSamples = MAX_FREQUENCY_INPUT_POINTS) {
  if (!series || !Array.isArray(series.times) || series.times.length <= maxSamples) return series;
  const step = Math.ceil(series.times.length / maxSamples);
  const pick = (arr) => {
    if (!Array.isArray(arr)) return arr;
    const out = [];
    for (let i = 0; i < arr.length; i += step) out.push(arr[i]);
    return out;
  };
  return Object.fromEntries(
    Object.entries(series).map(([key, value]) => [key, Array.isArray(value) ? pick(value) : value])
  );
}

function trBand(r, key, fallback) {
  const n = Number(r?.[key]);
  return Number.isFinite(n) ? n : fallback;
}

/** @param {'ok' | 'warn' | 'neutral'} status */
function summaryCapsuleToneClass(status) {
  if (status === 'ok') return 'suspension-summary-capsule--good';
  if (status === 'warn') return 'suspension-summary-capsule--bad';
  return 'suspension-summary-capsule--neutral';
}

/** Worst of several tuning statuses (warn beats ok/neutral). */
function worstTuningStatus(...statuses) {
  if (statuses.some((s) => s === 'warn')) return 'warn';
  if (statuses.length && statuses.every((s) => s === 'ok')) return 'ok';
  if (statuses.some((s) => s === 'ok')) return 'ok';
  return 'neutral';
}

function capsuleToneExplanation(status) {
  if (status === 'ok') {
    return 'Green: inside your acceptable band (Settings → Advanced), or the rule passed.';
  }
  if (status === 'warn') {
    return 'Red: outside your band or a warning rule fired—review the numbers below.';
  }
  return 'Amber: missing data, rule not applied, or neutral—see details below.';
}

function computeTooltipFixedStyle(anchorEl) {
  if (!anchorEl) return null;
  const r = anchorEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const w = Math.min(300, vw - 16);
  const left = Math.max(8, Math.min(r.right - w, vw - w - 8));
  const top = r.top - 6;
  const maxH = Math.min(320, Math.max(100, r.top - 10));
  return {
    position: 'fixed',
    left,
    top,
    width: w,
    maxHeight: maxH,
    transform: 'translateY(-100%)',
    zIndex: 50000,
  };
}

/** Portal tooltip so parent overflow:hidden does not clip it. */
function SummaryCapsuleTooltip({ label, infoText }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [fixedStyle, setFixedStyle] = useState(null);
  const closeT = useRef(null);

  const clearClose = useCallback(() => {
    if (closeT.current) {
      clearTimeout(closeT.current);
      closeT.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearClose();
    closeT.current = setTimeout(() => {
      setOpen(false);
      setFixedStyle(null);
    }, 260);
  }, [clearClose]);

  const openNow = useCallback(() => {
    clearClose();
    const el = wrapRef.current;
    setFixedStyle(el ? computeTooltipFixedStyle(el) : null);
    setOpen(true);
  }, [clearClose]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const ro = () => {
      const el = wrapRef.current;
      if (el) setFixedStyle(computeTooltipFixedStyle(el));
    };
    window.addEventListener('scroll', ro, true);
    window.addEventListener('resize', ro);
    return () => {
      window.removeEventListener('scroll', ro, true);
      window.removeEventListener('resize', ro);
    };
  }, [open]);

  const text =
    infoText != null && String(infoText).trim() !== ''
      ? String(infoText).trim()
      : `No explanation is available for «${label}».`;

  return (
    <>
      <div
        ref={wrapRef}
        className="suspension-summary-capsule-help-wrap"
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
      >
        <button
          type="button"
          className="suspension-summary-capsule-help"
          aria-label={`Explain ${label}`}
          onFocus={openNow}
          onBlur={scheduleClose}
        >
          <FaQuestionCircle aria-hidden />
        </button>
      </div>
      {open &&
        fixedStyle &&
        createPortal(
          <div
            role="tooltip"
            className="suspension-summary-tooltip-portal"
            style={fixedStyle}
            onMouseEnter={clearClose}
            onMouseLeave={scheduleClose}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}

function SummaryMetricCapsule({ label, value, valueSub, status, infoText }) {
  const tone = summaryCapsuleToneClass(status);
  return (
    <div className={`suspension-summary-capsule suspension-summary-capsule--compact ${tone}`}>
      <SummaryCapsuleTooltip label={label} infoText={infoText} />
      <div className="suspension-summary-capsule-value-block">
        <div className="suspension-summary-capsule-value">{value}</div>
        {valueSub ? <div className="suspension-summary-capsule-value-sub">{valueSub}</div> : null}
      </div>
      <div className="suspension-summary-capsule-label">{label}</div>
    </div>
  );
}

/** @param {{ key: string, sideClass: string, tag: string, primary: React.ReactNode, secondary?: React.ReactNode }[]} lines */
function SummaryPairedCapsule({ label, status, infoText, lines }) {
  const tone = summaryCapsuleToneClass(status);
  return (
    <div className={`suspension-summary-capsule suspension-summary-capsule--compact suspension-summary-capsule--paired ${tone}`}>
      <SummaryCapsuleTooltip label={label} infoText={infoText} />
      <div className="suspension-summary-capsule-paired-lines">
        {lines.map(({ key, sideClass, tag, primary, secondary }) => (
          <div key={key} className={`suspension-summary-pair-stack-line ${sideClass}`}>
            {tag ? <span className="suspension-summary-pair-tag">{tag}</span> : null}
            <div className="suspension-summary-pair-stack-values">
              <div className="suspension-summary-pair-primary">{primary}</div>
              {secondary != null && secondary !== '' ? (
                <div className="suspension-summary-pair-secondary">{secondary}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <div className="suspension-summary-capsule-label">{label}</div>
    </div>
  );
}

function SuspensionKbRecCard({ observation, recommendation }) {
  return (
    <div className="suspension-rec-card">
      <div className="suspension-rec-statement">{observation}</div>
      {recommendation ? <div className="suspension-rec-recommendation">{recommendation}</div> : null}
    </div>
  );
}

function SuspensionKbBucket({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="suspension-rec-bucket">
      <div className="suspension-rec-bucket-title">{title}</div>
      <div className="suspension-rec-bucket-cards">
        {items.map((it, i) => (
          <SuspensionKbRecCard key={i} observation={it.observation} recommendation={it.recommendation} />
        ))}
      </div>
    </div>
  );
}

function SuspensionKbSideColumn({ heading, accentClass, sideSlices }) {
  const n =
    (sideSlices?.compression?.length || 0) +
    (sideSlices?.rebound?.length || 0) +
    (sideSlices?.other?.length || 0);
  if (n === 0) return null;
  return (
    <div className={`suspension-rec-side ${accentClass}`}>
      <div className="suspension-rec-side-head">{heading}</div>
      <SuspensionKbBucket title="Compression" items={sideSlices.compression} />
      <SuspensionKbBucket title="Rebound" items={sideSlices.rebound} />
      <SuspensionKbBucket title="Other" items={sideSlices.other} />
    </div>
  );
}

function SuspensionRecommendationsBlock({ kbSlices }) {
  if (!kbSlices) return null;
  const general = kbSlices.general || [];
  const hasGeneral = general.length > 0;
  const hasFront =
    (kbSlices.front?.compression?.length || 0) +
      (kbSlices.front?.rebound?.length || 0) +
      (kbSlices.front?.other?.length || 0) >
    0;
  const hasRear =
    (kbSlices.rear?.compression?.length || 0) +
      (kbSlices.rear?.rebound?.length || 0) +
      (kbSlices.rear?.other?.length || 0) >
    0;

  return (
    <div className="suspension-rec-root">
      {hasGeneral ? (
        <div className="suspension-rec-general">
          <div className="suspension-rec-section-label">Chassis · both ends</div>
          <div className="suspension-rec-general-cards">
            {general.map((it, i) => (
              <SuspensionKbRecCard key={i} observation={it.observation} recommendation={it.recommendation} />
            ))}
          </div>
        </div>
      ) : null}
      {hasFront || hasRear ? (
        <div className="suspension-rec-sides">
          <SuspensionKbSideColumn heading="Front" accentClass="suspension-rec-side--front" sideSlices={kbSlices.front} />
          <SuspensionKbSideColumn heading="Rear" accentClass="suspension-rec-side--rear" sideSlices={kbSlices.rear} />
        </div>
      ) : null}
    </div>
  );
}

function TuningInlineIcon({ status, title }) {
  if (status === 'neutral' || status == null) return null;
  if (status === 'ok') {
    return <FaThumbsUp className="tuning-inline-icon tuning-inline-icon--ok" title={title} aria-label={title || 'Within range'} />;
  }
  return (
    <FaExclamationTriangle
      className="tuning-inline-icon tuning-inline-icon--warn"
      title={title}
      aria-label={title || 'Outside range'}
    />
  );
}

function StatStackRow({ label, value, status = 'neutral' }) {
  return (
    <div className="suspension-jack-stat-stack-row">
      <div className="suspension-jack-stat-stack-label">{label}</div>
      <div className="suspension-jack-stat-stack-value tuning-stat-value-with-icon">
        <TuningInlineIcon
          status={status}
          title={status === 'ok' ? 'Within your acceptable band (Settings)' : 'Outside your acceptable band (Settings)'}
        />
        <span>{value}</span>
      </div>
    </div>
  );
}

function StatStackSection({ title }) {
  return <div className="suspension-jack-stat-stack-section">{title}</div>;
}

function svgRange(values, fallback = { min: 0, max: 1 }) {
  let min = Infinity;
  let max = -Infinity;
  for (const value of values) {
    const n = Number(value);
    if (!Number.isFinite(n)) continue;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  if (min === Infinity) return fallback;
  if (min === max) return { min: min - 1, max: max + 1 };
  return { min, max };
}

function BybSvgGrid() {
  return (
    <>
      {[0, 25, 50, 75, 100].map((x) => <line key={`v-${x}`} x1={x} y1="0" x2={x} y2="100" className="byb-susp-grid-line" />)}
      {[0, 25, 50, 75, 100].map((y) => <line key={`h-${y}`} x1="0" y1={y} x2="100" y2={y} className="byb-susp-grid-line" />)}
      <line x1="0" y1="50" x2="100" y2="50" className="byb-susp-zero-line" />
      <line x1="0" y1="0" x2="0" y2="100" className="byb-susp-red-axis" />
    </>
  );
}

function BybScatterFrame({ title, xLabel, yLabel, children }) {
  return (
    <div className="byb-susp-chart-frame">
      <div className="byb-susp-chart-head">
        <span>{title}</span>
      </div>
      <div className="byb-susp-chart-body">
        <div className="byb-susp-y-label">{yLabel}</div>
        {children}
        <div className="byb-susp-x-label">{xLabel}</div>
      </div>
    </div>
  );
}

function BybScatterPlot({ rows, xGetter, yGetter, color = '#c8f3ff', xDomain = null, yDomain = null, lines = false }) {
  const points = (rows || [])
    .map((row) => ({ x: Number(xGetter(row)), y: Number(yGetter(row)) }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const xr = xDomain || svgRange(points.map((p) => p.x));
  const yr = yDomain || svgRange(points.map((p) => p.y));
  const mapPoint = (p) => ({
    x: ((p.x - xr.min) / (xr.max - xr.min)) * 100,
    y: 100 - ((p.y - yr.min) / (yr.max - yr.min)) * 100,
  });
  const path = lines
    ? points.map((p, i) => {
      const m = mapPoint(p);
      return `${i === 0 ? 'M' : 'L'} ${m.x.toFixed(3)} ${m.y.toFixed(3)}`;
    }).join(' ')
    : '';
  return (
    <svg className="byb-susp-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
      <BybSvgGrid />
      {lines ? <path d={path} fill="none" stroke={color} strokeWidth="0.18" opacity="0.58" vectorEffect="non-scaling-stroke" /> : null}
      {!lines ? points.map((p, i) => {
        const m = mapPoint(p);
        return <circle key={i} cx={m.x} cy={m.y} r="0.11" fill={color} opacity="0.32" />;
      }) : null}
    </svg>
  );
}

function percentile(values, pct) {
  const finite = (values || []).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (!finite.length) return null;
  const idx = Math.min(finite.length - 1, Math.max(0, Math.round((pct / 100) * (finite.length - 1))));
  return finite[idx];
}

function AutoTuningPanel({ tuningEval, summarySnapshot, analysisResults, tuningRanges }) {
  const [riderLevel, setRiderLevel] = useState('intermediate');
  const [terrain, setTerrain] = useState('mixed');

  const recommendations = useMemo(() => {
    const ranges = tuningRanges || {};
    const levelBias = { beginner: -2, intermediate: 0, advanced: 2 }[riderLevel] || 0;
    const terrainBias = { smooth: -1, mixed: 0, rough: 2 }[terrain] || 0;
    const sagBaseMin = trBand(ranges, 'tuneDynamicSagPctMin', 25);
    const sagBaseMax = trBand(ranges, 'tuneDynamicSagPctMax', 35);
    const targetFrontMin = sagBaseMin + levelBias + terrainBias;
    const targetFrontMax = sagBaseMax + levelBias + terrainBias;
    const targetRearMin = targetFrontMin + 2;
    const targetRearMax = targetFrontMax + 2;
    const minTravelUse = trBand(ranges, 'tuneMinTravelUsePct', 70);
    const bottomLimitBase = trBand(ranges, 'tuneMaxBottomOutsPerEnd', 2);
    const bottomLimit = Math.max(0, bottomLimitBase + (terrain === 'rough' ? 2 : terrain === 'smooth' ? -1 : 0));
    const packGood = trBand(ranges, 'tunePackingIndexGoodMax', 30);
    const rebMin = trBand(ranges, 'tuneReboundP95Min', 1000);
    const rebMax = trBand(ranges, 'tuneReboundP95Max', 1400);
    const compMin = trBand(ranges, 'tuneCompressionP95Min', 2500);
    const compMax = trBand(ranges, 'tuneCompressionP95Max', 4000);
    const sagF = Number(summarySnapshot.dynSagFrontPct);
    const sagR = Number(summarySnapshot.dynSagRearPct);
    const travelF = Number(summarySnapshot.travelFrontPct);
    const travelR = Number(summarySnapshot.travelRearPct);
    const bottomF = Number(summarySnapshot.bottomF) || 0;
    const bottomR = Number(summarySnapshot.bottomR) || 0;
    const packing = Number(summarySnapshot.packingIndex);
    const topF = Number(summarySnapshot.topF) || 0;
    const topR = Number(summarySnapshot.topR) || 0;

    const statsF = analysisResults?.stats?.front || null;
    const statsR = analysisResults?.stats?.rear || null;
    const packF = Number(analysisResults?.frontPackingIndex);
    const packR = Number(analysisResults?.rearPackingIndex);
    const ok = (value, note = 'No change', evidence = '') => ({ marker: 2, tone: 'ok', value, note, evidence });
    const move = (marker, value, note, evidence = '') => ({ marker, tone: 'warn', value, note, evidence });
    const missing = (note = 'Needs more data') => ({ marker: 2, tone: 'neutral', value: '—', note, evidence: '' });
    const sideText = (side) => (side === 'fork' ? 'Front' : 'Rear');
    const sideData = {
      fork: { sag: sagF, travel: travelF, bottom: bottomF, top: topF, stats: statsF, packing: Number.isFinite(packF) ? packF : packing },
      shock: { sag: sagR, travel: travelR, bottom: bottomR, top: topR, stats: statsR, packing: Number.isFinite(packR) ? packR : packing },
    };
    const vel = (side) => sideData[side].stats?.velocity || {};
    const comp = (side) => vel(side).compression || {};
    const reb = (side) => vel(side).rebound || {};
    const p90DepthPct = (side) => {
      const depths = comp(side).allStrokes?.map((s) => Number(s.depth)).filter(Number.isFinite) || [];
      const p90 = percentile(depths, 90);
      const travelMm =
        side === 'fork'
          ? Number(analysisResults?.params?.frontTravel) || SUMMARY_DEFAULT_TRAVEL_FRONT_MM
          : Number(analysisResults?.params?.rearTravel) || SUMMARY_DEFAULT_TRAVEL_REAR_MM;
      return Number.isFinite(p90) && travelMm > 0 ? (p90 / travelMm) * 100 : null;
    };

    const sagRec = (side, sag, min, max) => {
      if (!Number.isFinite(sag)) return missing('Record enough travel to estimate sag.');
      const deltaLow = min - sag;
      const deltaHigh = sag - max;
      if (deltaLow > 5) return move(0, `${formatNum(sag, 1)}%`, `${sideText(side)} rides very high: reduce spring pressure/preload or spring rate.`, `Target ${min}-${max}%`);
      if (deltaLow > 0) return move(1, `${formatNum(sag, 1)}%`, `${sideText(side)} rides high: reduce spring pressure/preload slightly.`, `Target ${min}-${max}%`);
      if (deltaHigh > 5) return move(4, `${formatNum(sag, 1)}%`, `${sideText(side)} rides very low: increase spring pressure/preload or spring rate.`, `Target ${min}-${max}%`);
      if (deltaHigh > 0) return move(3, `${formatNum(sag, 1)}%`, `${sideText(side)} rides low: increase spring pressure/preload slightly.`, `Target ${min}-${max}%`);
      return ok(`${formatNum(sag, 1)}%`, `Inside ${min}-${max}% target.`, `Target ${min}-${max}%`);
    };

    const springRec = (side, sag, travel, bottom) => {
      const min = side === 'fork' ? targetFrontMin : targetRearMin;
      const max = side === 'fork' ? targetFrontMax : targetRearMax;
      if (!Number.isFinite(sag) && !Number.isFinite(travel)) return missing('Needs sag or travel-use data.');
      if (bottom > bottomLimit || (Number.isFinite(travel) && travel > 98)) {
        return move(bottom > bottomLimit * 2 ? 4 : 3, bottom ? `${bottom} BO` : `${formatNum(travel, 0)}%`, `${sideText(side)} needs more spring/progression before damping masks bottom-outs.`, `Limit ${bottomLimit}; travel ${formatNum(travel, 1)}%`);
      }
      return sagRec(side, sag, min, max);
    };

    const hscRec = (side) => {
      const d = sideData[side];
      const p90 = p90DepthPct(side);
      const cp95 = Number(comp(side).p95);
      if (d.bottom > bottomLimit || (Number.isFinite(p90) && p90 > 92)) {
        return move(d.bottom > bottomLimit * 2 ? 4 : 3, 'Add HSC', `${sideText(side)} is deep in the stroke: add HSC/progression after spring check.`, `p90 depth ${formatNum(p90, 1)}%, BO ${d.bottom}`);
      }
      if (Number.isFinite(d.travel) && d.travel < minTravelUse && d.bottom === 0) {
        return move(d.travel < minTravelUse - 15 ? 0 : 1, 'Open HSC', `${sideText(side)} is not using travel: reduce HSC/end-stroke resistance.`, `Max travel ${formatNum(d.travel, 1)}%`);
      }
      if (Number.isFinite(cp95) && cp95 > compMax && Number.isFinite(d.travel) && d.travel > 85) {
        return move(3, 'Add HSC', `${sideText(side)} compression p95 is high with high travel use; add HSC cautiously.`, `Comp p95 ${cp95.toFixed(0)} mm/s`);
      }
      if (Number.isFinite(cp95) && cp95 < compMin && Number.isFinite(d.travel) && d.travel < minTravelUse) {
        return move(1, 'Open HSC', `${sideText(side)} compression demand is low and travel use is low; open HSC.`, `Comp p95 ${cp95.toFixed(0)} mm/s`);
      }
      return ok('Hold', 'No clear HSC change from this run.', Number.isFinite(p90) ? `p90 depth ${formatNum(p90, 1)}%` : '');
    };

    const lscRec = (side) => {
      const d = sideData[side];
      const max = side === 'fork' ? targetFrontMax : targetRearMax;
      const min = side === 'fork' ? targetFrontMin : targetRearMin;
      if (!Number.isFinite(d.sag)) return missing('Needs dynamic sag data.');
      if (d.sag > max + 4) return move(3, 'Add LSC', `${sideText(side)} sits deep dynamically; add LSC only after spring/sag is close.`, `Sag ${formatNum(d.sag, 1)}%`);
      if (d.sag < min - 4 && Number.isFinite(d.travel) && d.travel < minTravelUse) {
        return move(1, 'Open LSC', `${sideText(side)} rides high and uses little travel; reduce LSC after spring check.`, `Sag ${formatNum(d.sag, 1)}%`);
      }
      return ok('Hold', 'Chassis support looks acceptable from this run.', `Sag ${formatNum(d.sag, 1)}%`);
    };

    const hsrRec = (side) => {
      const d = sideData[side];
      const rp95 = Number(reb(side).p95);
      const cp95 = Number(comp(side).p95);
      if (Number.isFinite(d.packing) && d.packing > packGood + 15) {
        return move(1, 'Faster', `${sideText(side)} shows packing: open high-speed rebound so it recovers.`, `Packing ${formatNum(d.packing, 0)}`);
      }
      if (Number.isFinite(rp95) && Number.isFinite(cp95) && rp95 >= cp95) {
        return move(4, 'Slower', `${sideText(side)} rebound p95 is at/above compression p95: add rebound damping.`, `Reb ${rp95.toFixed(0)} vs comp ${cp95.toFixed(0)}`);
      }
      if (Number.isFinite(rp95) && rp95 > rebMax) {
        return move(3, 'Slower', `${sideText(side)} rebound p95 is above the reference band: slow HSR.`, `Band ${rebMin}-${rebMax}, current ${rp95.toFixed(0)}`);
      }
      if (Number.isFinite(rp95) && rp95 < rebMin) {
        return move(1, 'Faster', `${sideText(side)} rebound p95 is below band: open HSR unless spring is too stiff.`, `Band ${rebMin}-${rebMax}, current ${rp95.toFixed(0)}`);
      }
      if (d.top > 0) return move(3, 'Slower', `${sideText(side)} top-outs are present: slow rebound slightly.`, `Top-outs ${d.top}`);
      return ok('Hold', 'No clear HSR change from this run.', Number.isFinite(rp95) ? `Reb p95 ${rp95.toFixed(0)} mm/s` : '');
    };

    const lsrRec = (side) => {
      const d = sideData[side];
      const compCount = Number(comp(side).count);
      const rebCount = Number(reb(side).count);
      if (Number.isFinite(compCount) && Number.isFinite(rebCount) && rebCount > 0 && compCount / rebCount > 1.35) {
        return move(1, 'Faster', `${sideText(side)} has more compression than rebound strokes: open LSR to reduce packing.`, `Count ratio ${(compCount / rebCount).toFixed(2)}`);
      }
      if (Number.isFinite(d.packing) && d.packing > packGood) {
        return move(1, 'Faster', `${sideText(side)} packing index is above target: speed low-speed rebound.`, `Packing ${formatNum(d.packing, 0)} > ${packGood}`);
      }
      if (d.top > 0) return move(3, 'Slower', `${sideText(side)} top-outs suggest rebound can be slowed.`, `Top-outs ${d.top}`);
      return ok('Hold', 'No clear LSR change from this run.');
    };

    return [
      {
        label: 'Spring / sag',
        target: `Fork ${targetFrontMin}-${targetFrontMax}% · Shock ${targetRearMin}-${targetRearMax}%`,
        fork: springRec('fork', sagF, travelF, bottomF),
        shock: springRec('shock', sagR, travelR, bottomR),
      },
      {
        label: 'High-speed compression',
        target: 'Controls sharp hits and end-stroke support',
        fork: hscRec('fork'),
        shock: hscRec('shock'),
      },
      {
        label: 'Low-speed compression',
        target: 'Controls chassis support and rider inputs',
        fork: lscRec('fork'),
        shock: lscRec('shock'),
      },
      {
        label: 'High-speed rebound',
        target: 'Recover from repeated impacts without kicking back',
        fork: hsrRec('fork'),
        shock: hsrRec('shock'),
      },
      {
        label: 'Low-speed rebound',
        target: 'Controls body movement after slower compressions',
        fork: lsrRec('fork'),
        shock: lsrRec('shock'),
      },
    ];
  }, [analysisResults, riderLevel, summarySnapshot, terrain, tuningRanges]);

  const renderScale = (rec, side) => (
    <div className={`suspension-auto-scale suspension-auto-scale--${side} suspension-auto-scale--${rec.tone}`} title={rec.note}>
      {[0, 1, 2, 3, 4].map((idx) => <span key={idx} className={idx === rec.marker ? 'is-active' : ''} />)}
      <div className="suspension-auto-scale-value">{rec.value}</div>
      <small>{rec.marker < 2 ? 'Reduce' : rec.marker > 2 ? 'Increase' : 'Ok'}</small>
    </div>
  );

  return (
    <div className="suspension-panel-tab-content suspension-auto-layout" role="tabpanel">
      <div className="suspension-auto-controls">
        <label>
          <span>Rider level</span>
          <select value={riderLevel} onChange={(e) => setRiderLevel(e.target.value)}>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </label>
        <label>
          <span>Terrain</span>
          <select value={terrain} onChange={(e) => setTerrain(e.target.value)}>
            <option value="smooth">Smooth</option>
            <option value="mixed">Mixed</option>
            <option value="rough">Rough</option>
          </select>
        </label>
        <div className="suspension-auto-status">
          Live recommendations from current suspension metrics and Jack stroke analysis.
        </div>
      </div>
      <main className="suspension-auto-board">
        <div className="suspension-auto-columns"><span>Adjustment</span><span>Front</span><span>Rear</span></div>
        {recommendations.map((row) => (
          <div key={row.label} className="suspension-auto-row">
            <div className="suspension-auto-row-label">
              <strong>{row.label}</strong>
              <span>{row.target}</span>
            </div>
            {renderScale(row.fork, 'front')}
            {renderScale(row.shock, 'rear')}
            <div className="suspension-auto-note suspension-auto-note--front">{row.fork.note}</div>
            <div className="suspension-auto-note suspension-auto-note--rear">{row.shock.note}</div>
            {row.fork.evidence ? <div className="suspension-auto-evidence suspension-auto-evidence--front">{row.fork.evidence}</div> : null}
            {row.shock.evidence ? <div className="suspension-auto-evidence suspension-auto-evidence--rear">{row.shock.evidence}</div> : null}
          </div>
        ))}
      </main>
    </div>
  );
}

/**
 * Same numbers as jack-benchmark.html renderStatsTable(); single vertical stack (label above value).
 */
function JackBenchmarkStatsTable({ sideLabel, stats, totalTravelMm, rowStatusMap = {} }) {
  if (!stats?.position || !stats?.velocity) {
    return <p className="suspension-jack-empty">No statistics for {sideLabel.toLowerCase()}.</p>;
  }
  const pos = stats.position;
  const vel = stats.velocity;
  const comp = vel.compression;
  const reb = vel.rebound;
  if (!comp || !reb) {
    return <p className="suspension-jack-empty">No statistics for {sideLabel.toLowerCase()}.</p>;
  }

  const totalTravel = Number(totalTravelMm);
  const maxTravelPercent = (pos.max / totalTravel * 100).toFixed(1);
  const dynamicSagPercent = (pos.mean / totalTravel * 100).toFixed(1);

  const rs = (section, label) => {
    const key = jackRowKeyForLabel(section, label);
    return key && rowStatusMap[key] != null ? rowStatusMap[key] : 'neutral';
  };

  return (
    <div className="suspension-jack-stat-stack" aria-label={`${sideLabel} suspension statistics`}>
      <StatStackSection title="Position" />
      <StatStackRow
        label="Max Travel"
        value={`${pos.max.toFixed(1)} mm (${maxTravelPercent}%)`}
        status={rs('Position', 'Max Travel')}
      />
      <StatStackRow
        label="Dynamic Sag"
        value={`${pos.mean.toFixed(1)} mm (${dynamicSagPercent}%)`}
        status={rs('Position', 'Dynamic Sag')}
      />
      <StatStackRow label="95th %" value={`${pos.p95.toFixed(1)} mm`} />
      <StatStackRow label="Std Dev" value={`${pos.std.toFixed(1)} mm`} />
      <StatStackRow label="Total Movement" value={`${(pos.totalMovement / 1000).toFixed(1)} m`} />

      <StatStackSection title="Compression" />
      <StatStackRow label="Stroke Count" value={String(comp.count)} status={rs('Compression', 'Stroke Count')} />
      <StatStackRow label="Avg Stroke" value={`${comp.depthMean?.toFixed(1) || 0} mm`} />
      <StatStackRow label="Avg Max Vel" value={`${comp.mean.toFixed(0)} mm/s`} />
      <StatStackRow label="Peak Vel" value={`${comp.max.toFixed(0)} mm/s`} />
      <StatStackRow label="95th % Vel" value={`${comp.p95.toFixed(0)} mm/s`} status={rs('Compression', '95th % Vel')} />
      <StatStackRow label="Bottom-outs" value={String(comp.bottomOutCount || 0)} status={rs('Compression', 'Bottom-outs')} />

      <StatStackSection title="Rebound" />
      <StatStackRow label="Stroke Count" value={String(reb.count)} status={rs('Rebound', 'Stroke Count')} />
      <StatStackRow label="Avg Stroke" value={`${reb.depthMean?.toFixed(1) || 0} mm`} />
      <StatStackRow label="Avg Max Vel" value={`${reb.mean.toFixed(0)} mm/s`} />
      <StatStackRow label="Peak Vel" value={`${reb.max.toFixed(0)} mm/s`} />
      <StatStackRow label="95th % Vel" value={`${reb.p95.toFixed(0)} mm/s`} status={rs('Rebound', '95th % Vel')} />
      <StatStackRow label="Top-outs" value={String(reb.topOutCount || 0)} />
    </div>
  );
}

export default function SuspensionAnalysisPanel({
  telemetryData = [],
  jackParams,
  /** When set (e.g. from RideView), reuse this result instead of re-running the Jack pipeline */
  jackAnalysisResult = null,
  onSeek,
  /** Data timeline offset (s): video time = data time − dataOffset (matches Jack Plotly / handleSeek). */
  dataOffset = 0,
  /** Packing event in data time (s); parent zooms Jack charts ±1.5s like jack-benchmark.html */
  onJackPackingEventSelect,
  initialAiInsight = '',
  onSaveAiInsight,
  /**
   * When set, tab strip is hidden and this tab is shown (RideView agnostic panels).
   * @type {string | null}
   */
  embeddedTab = null,
}) {
  /** Top-level tabs. Summary = one-screen overview; Statistics = Jack benchmark tables. */
  const [panelTab, setPanelTab] = useState(embeddedTab || 'summary');

  useEffect(() => {
    if (embeddedTab) setPanelTab(embeddedTab);
  }, [embeddedTab]);

  useEffect(() => {
    if (!embeddedTab && panelTab === 'imu') setPanelTab('summary');
  }, [embeddedTab, panelTab]);
  const [openAiLoading, setOpenAiLoading] = useState(false);
  const [openAiError, setOpenAiError] = useState('');
  const [openAiText, setOpenAiText] = useState(initialAiInsight || '');
  const [roughnessCalKey, setRoughnessCalKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchRoughnessCalibration().then(() => {
      if (!cancelled) setRoughnessCalKey((k) => k + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setOpenAiText(typeof initialAiInsight === 'string' ? initialAiInsight : '');
  }, [initialAiInsight]);

  const { metrics } = useMemo(() => {
    const s = buildSuspensionSeries(telemetryData);
    if (!s) return { metrics: null };
    const ev = detectSuspensionEvents(s);
    const m = computeSuspensionMetrics(s, ev);
    return { metrics: m };
  }, [telemetryData]);

  const effectiveJackParams = useMemo(
    () => getJackParamsForAnalysis(jackParams && typeof jackParams === 'object' ? jackParams : {}),
    [jackParams],
  );

  const jackResult = useMemo(() => {
    if (jackAnalysisResult != null) return jackAnalysisResult;
    return runJackPackingAnalysisFromTelemetry(telemetryData, effectiveJackParams);
  }, [jackAnalysisResult, telemetryData, effectiveJackParams]);

  const displayProcessedData = useMemo(
    () => downsampleRows(jackResult?.processedData || [], MAX_EMBEDDED_CHART_POINTS),
    [jackResult]
  );

  const frequencySeries = useMemo(
    () => downsampleSuspensionSeries(buildSuspensionSeries(telemetryData), MAX_FREQUENCY_INPUT_POINTS),
    [telemetryData]
  );

  const jackEventsList = useMemo(() => {
    if (!jackResult?.ok || !jackResult.analysisResults) return [];
    const { front = [], rear = [] } = jackResult.analysisResults.packingEvents || {};
    return [
      ...front.map((e, i) => ({ ...e, side: 'Front', key: `f-${i}` })),
      ...rear.map((e, i) => ({ ...e, side: 'Rear', key: `r-${i}` })),
    ].sort((a, b) => a.start_time - b.start_time);
  }, [jackResult]);

  const jackSummary = useMemo(() => {
    if (!jackResult?.ok || !jackResult.analysisResults) return null;
    const {
      packingEvents = { front: [], rear: [] },
      totalImpacts = { front: 0, rear: 0 },
      packedImpacts = { front: 0, rear: 0 },
      totalPackedTime = { front: 0, rear: 0 },
      totalRunTime = 0,
    } = jackResult.analysisResults;
    const allEvents = [...(packingEvents.front || []), ...(packingEvents.rear || [])];
    const avgSeverity = allEvents.length
      ? allEvents.reduce((sum, ev) => sum + (Number(ev.severity) || 0), 0) / allEvents.length
      : 0;
    const severityValues = allEvents.map((ev) => Number(ev.severity) || 0);
    let worstSeverity = 0;
    for (let i = 0; i < severityValues.length; i += 1) {
      if (severityValues[i] > worstSeverity) worstSeverity = severityValues[i];
    }
    const totalPackedImpacts = (Number(packedImpacts.front) || 0) + (Number(packedImpacts.rear) || 0);
    const totalImpactsCount = (Number(totalImpacts.front) || 0) + (Number(totalImpacts.rear) || 0);
    const totalPackedDuration = (Number(totalPackedTime.front) || 0) + (Number(totalPackedTime.rear) || 0);
    const packingRatio = totalImpactsCount > 0 ? (totalPackedImpacts / totalImpactsCount) * 100 : 0;
    const timePackedPercent = totalRunTime > 0 ? (totalPackedDuration / totalRunTime) * 100 : 0;
    return {
      packingRatio,
      avgSeverity,
      worstSeverity,
      timePackedPercent,
      packingEvents: allEvents.length,
      totalImpacts: totalImpactsCount,
    };
  }, [jackResult]);

  const summarySnapshot = useMemo(() => {
    const cal = getRoughnessCalibrationSync();
    const rough = computeRoughnessFromTelemetry(telemetryData, cal);
    return buildSummarySnapshot(jackResult, metrics, rough.score);
  }, [jackResult, metrics, telemetryData, roughnessCalKey]);

  const tuningEval = useMemo(() => {
    const ranges = getTuningGuideConfig();
    const jOk = jackResult?.ok === true;
    const ar = jOk ? jackResult.analysisResults : null;
    const stF = ar?.stats?.front ?? null;
    const stR = ar?.stats?.rear ?? null;
    const frontTravelMm =
      Number.isFinite(Number(ar?.params?.frontTravel)) && Number(ar.params.frontTravel) > 0
        ? Number(ar.params.frontTravel)
        : SUMMARY_DEFAULT_TRAVEL_FRONT_MM;
    const rearTravelMm =
      Number.isFinite(Number(ar?.params?.rearTravel)) && Number(ar.params.rearTravel) > 0
        ? Number(ar.params.rearTravel)
        : SUMMARY_DEFAULT_TRAVEL_REAR_MM;
    return analyzeTuningGuide(summarySnapshot, stF, stR, frontTravelMm, rearTravelMm, ranges);
  }, [summarySnapshot, jackResult]);

  const handleOpenAiInsight = useCallback(async () => {
    setOpenAiError('');
    const ranges = getTuningGuideConfig();
    const apiKey = resolveOpenAiApiKey(ranges);
    const jOk = jackResult?.ok === true;
    const ar = jOk ? jackResult.analysisResults : null;
    const stF = ar?.stats?.front ?? null;
    const stR = ar?.stats?.rear ?? null;
    const frontTravelMm =
      Number.isFinite(Number(ar?.params?.frontTravel)) && Number(ar.params.frontTravel) > 0
        ? Number(ar.params.frontTravel)
        : SUMMARY_DEFAULT_TRAVEL_FRONT_MM;
    const rearTravelMm =
      Number.isFinite(Number(ar?.params?.rearTravel)) && Number(ar.params.rearTravel) > 0
        ? Number(ar.params.rearTravel)
        : SUMMARY_DEFAULT_TRAVEL_REAR_MM;
    const payload = buildOpenAiPayload(summarySnapshot, stF, stR, frontTravelMm, rearTravelMm, tuningEval.kbBullets);
    setOpenAiLoading(true);
    try {
      const text = await fetchOpenAiTuningInsight(apiKey, payload);
      setOpenAiText(text);
      if (typeof onSaveAiInsight === 'function') {
        onSaveAiInsight(text);
      }
    } catch (e) {
      setOpenAiError(e?.message || String(e));
    } finally {
      setOpenAiLoading(false);
    }
  }, [jackResult, summarySnapshot, tuningEval.kbBullets, onSaveAiInsight]);

  const openAiHtml = useMemo(() => formatOpenAiInsightHtml(openAiText), [openAiText]);

  const imuSeries = useMemo(() => {
    if (!Array.isArray(telemetryData) || telemetryData.length === 0) return [];
    return telemetryData
      .map((row) => {
        const tRaw = row.time ?? row.Time ?? row.t;
        const time = Number(tRaw);
        if (!Number.isFinite(time)) return null;
        const frameX = Number(row.frame_accel_x);
        const frameY = Number(row.frame_accel_y);
        const frameZ = Number(row.frame_accel_z);
        const barX = Number(row.bar_accel_x);
        const barY = Number(row.bar_accel_y);
        const barZ = Number(row.bar_accel_z);
        return {
          ...row,
          time,
          frame_accel_x: Number.isFinite(frameX) ? frameX : null,
          frame_accel_y: Number.isFinite(frameY) ? frameY : null,
          frame_accel_z: Number.isFinite(frameZ) ? frameZ : null,
          bar_accel_x: Number.isFinite(barX) ? barX : null,
          bar_accel_y: Number.isFinite(barY) ? barY : null,
          bar_accel_z: Number.isFinite(barZ) ? barZ : null,
        };
      })
      .filter(Boolean);
  }, [telemetryData]);

  const tuningRanges = useMemo(() => getTuningGuideConfig(), []);

  const tuningBands = useMemo(
    () => ({
      balMin: trBand(tuningRanges, 'tuneBalanceRatioMin', 0.75),
      balMax: trBand(tuningRanges, 'tuneBalanceRatioMax', 1.15),
      sagMin: trBand(tuningRanges, 'tuneDynamicSagPctMin', 25),
      sagMax: trBand(tuningRanges, 'tuneDynamicSagPctMax', 35),
      minTravel: trBand(tuningRanges, 'tuneMinTravelUsePct', 70),
      maxBoEnd: trBand(tuningRanges, 'tuneMaxBottomOutsPerEnd', 2),
      maxBoTotal: trBand(tuningRanges, 'tuneMaxBottomOutsTotal', 3),
      packGood: trBand(tuningRanges, 'tunePackingIndexGoodMax', 30),
      roughWarn: trBand(tuningRanges, 'tuneRoughnessWarnAbove', 0),
    }),
    [tuningRanges],
  );

  const summaryTooltips = useMemo(() => {
    const s = tuningEval.summary;
    const b = tuningBands;
    const snap = summarySnapshot;
    const toneLine = capsuleToneExplanation;

    const br = snap.balanceRatio;
    const balanceStatus = s.balance;
    const balance = `${toneLine(balanceStatus)}\n\nYour F/R mean travel ratio band: ${b.balMin}–${b.balMax}.\n\n${
      br != null && Number.isFinite(br)
        ? `Current ratio: ${formatNum(br, 2)} (${balanceStatus === 'ok' ? 'inside' : balanceStatus === 'warn' ? 'outside' : 'not scored'}).`
        : 'Current ratio: not available from this run.'
    }`;

    const dynPairStatus = worstTuningStatus(s.sagFront, s.sagRear);
    const fPct = snap.dynSagFrontPct;
    const rPct = snap.dynSagRearPct;
    const dynSag = `${toneLine(dynPairStatus)}\n\nYour dynamic sag band (% of wheel travel): ${b.sagMin}%–${b.sagMax}% (each end).\n\nFront: ${
      fPct == null
        ? 'no % computed.'
        : `${formatNum(fPct, 1)}% (${formatNum(snap.dynamicSagFrontMm, 1)} mm) — ${s.sagFront === 'ok' ? 'inside' : s.sagFront === 'warn' ? 'outside' : 'not scored'}.`
    }\nRear: ${
      rPct == null
        ? 'no % computed.'
        : `${formatNum(rPct, 1)}% (${formatNum(snap.dynamicSagRearMm, 1)} mm) — ${s.sagRear === 'ok' ? 'inside' : s.sagRear === 'warn' ? 'outside' : 'not scored'}.`
    }`;

    const trPairStatus = worstTuningStatus(s.travelFront, s.travelRear);
    const tf = snap.travelFrontPct;
    const tr = snap.travelRearPct;
    const travel = `${toneLine(trPairStatus)}\n\nYour minimum useful max-travel use: ${b.minTravel}% of stroke (per end), unless bottom-outs justify low use.\n\nFront: ${
      tf == null
        ? 'no %.'
        : `${formatNum(tf, 1)}% of stroke — ${s.travelFront === 'ok' ? 'OK' : s.travelFront === 'warn' ? 'low use warning' : 'neutral'}.`
    }\nRear: ${
      tr == null
        ? 'no %.'
        : `${formatNum(tr, 1)}% of stroke — ${s.travelRear === 'ok' ? 'OK' : s.travelRear === 'warn' ? 'low use warning' : 'neutral'}.`
    }`;

    const boCardStatus = worstTuningStatus(s.bottomFront, s.bottomRear, s.bottomTotal);
    const bf = snap.bottomF;
    const brw = snap.bottomR;
    const bottom = `${toneLine(boCardStatus)}\n\nPer-end limit: ≤${b.maxBoEnd} bottom-outs. Total F+R cap: ≤${b.maxBoTotal}.\n\nFront: ${
      bf == null ? 'no count.' : `${bf} (${s.bottomFront === 'ok' ? 'within per-end' : 'above per-end'}).`
    }\nRear: ${
      brw == null ? 'no count.' : `${brw} (${s.bottomRear === 'ok' ? 'within per-end' : 'above per-end'}).`
    }\nTotal F+R: ${
      bf == null && brw == null
        ? 'n/a'
        : `${(Number(bf) || 0) + (Number(brw) || 0)} (${s.bottomTotal === 'ok' ? 'within total cap' : 'above total cap'}).`
    }`;

    const top = `${toneLine(s.topBalance)}\n\nRule: rear top-out count should stay below front when both are available.\n\nFront: ${
      snap.topF != null ? snap.topF : '—'
    }\nRear: ${snap.topR != null ? snap.topR : '—'}\n\n${
      snap.topF == null || snap.topR == null
        ? 'Not enough data to evaluate.'
        : s.topBalance === 'ok'
          ? 'Rear < front: OK.'
          : 'Rear ≥ front: outside the guideline.'
    }`;

    const pi = snap.packingIndex;
    const packStatus = snap.jackOk ? s.packing : 'neutral';
    const packing = `${toneLine(packStatus)}\n\nGood packing index is at or below ${b.packGood} (0–100 scale; lower is better).\n\n${
      !snap.jackOk
        ? 'Jack packing analysis did not run—no index yet.'
        : pi == null
          ? 'No packing index in results.'
          : `Current index: ${formatNum(pi, 0)} (shown as ${Math.round(Number(pi))}% of the scale). Rating: ${snap.packingRating || '—'}.\n${s.packing === 'ok' ? 'Inside “good” cap.' : s.packing === 'warn' ? 'Above your good cap.' : 'Neutral.'}`
    }`;

    const rough = snap.roughness;
    const roughStatus = s.roughness;
    const roughness = `${toneLine(roughStatus)}\n\nScale 0–200: 0 calm, 50 typical trail, 100 quite rough (admin benchmarks tune mapping).\n\n${
      b.roughWarn <= 0
        ? 'Roughness warn is disabled (threshold 0 in Settings → Advanced).'
        : `Warn when roughness is above ${b.roughWarn}.`
    }\n\n${
      rough == null || !Number.isFinite(Number(rough))
        ? 'No roughness value for this run.'
        : `Current roughness: ${formatNum(rough, 1)} (${roughStatus === 'ok' ? 'below warn' : roughStatus === 'warn' ? 'above warn' : 'neutral'}).`
    }`;

    return { balance, dynSag, travel, bottom, top, packing, roughness };
  }, [tuningEval, tuningBands, summarySnapshot]);

  const hasSuspension = telemetryData.some(
    (d) =>
      (typeof d.fork === 'number' && Number.isFinite(d.fork)) ||
      (typeof d.shock === 'number' && Number.isFinite(d.shock)) ||
      (typeof d.fork_normalized === 'number' && Number.isFinite(d.fork_normalized)) ||
      (typeof d.shock_normalized === 'number' && Number.isFinite(d.shock_normalized))
  );

  if (!hasSuspension) {
    return (
      <div className="suspension-analysis-panel">
        <p className="suspension-jack-empty">No fork/shock data in this run.</p>
      </div>
    );
  }

  const histLabels = ['0–10%', '10–20%', '20–30%', '30–40%', '40–50%', '50–60%', '60–70%', '70–80%', '80–90%', '90–100%'];

  const PANEL_TABS = [
    { id: 'summary', label: 'Summary' },
    { id: 'ai-insight', label: 'AI insight' },
    { id: 'statistics', label: 'Statistics' },
    { id: 'packing', label: 'Packing' },
    { id: 'events', label: 'Events' },
    { id: 'balance', label: 'Balance' },
    { id: 'advanced', label: 'Advanced' },
    { id: 'imu', label: 'IMU', disabled: true },
  ];

  const effectiveTab = embeddedTab || panelTab;

  return (
    <div className={`suspension-analysis-panel ${embeddedTab ? 'suspension-analysis-panel--embedded' : ''}`}>
      {!embeddedTab ? (
        <div className="suspension-analysis-tabs suspension-analysis-tabs--flat" role="tablist" aria-label="Suspension analysis">
          {PANEL_TABS.map(({ id, label, disabled }) => (
            <button
              key={id}
              type="button"
              role="tab"
              disabled={disabled}
              aria-selected={panelTab === id}
              title={disabled ? 'Not available' : undefined}
              className={`suspension-analysis-tab ${panelTab === id ? 'suspension-analysis-tab--active' : ''} ${
                disabled ? 'suspension-analysis-tab--disabled' : ''
              }`}
              onClick={() => {
                if (!disabled) setPanelTab(id);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {effectiveTab === 'summary' && (
        <div className="suspension-panel-tab-content suspension-summary-tab" role="tabpanel">
          {!metrics && !summarySnapshot.jackOk && (
            <p className="suspension-jack-error">Not enough data for a summary.</p>
          )}
          {(metrics || summarySnapshot.jackOk) && (
            <>
              <div className="suspension-summary-metric-grid">
                <SummaryMetricCapsule
                  label="Balance (F/R)"
                  value={
                    summarySnapshot.balanceRatio != null && Number.isFinite(summarySnapshot.balanceRatio)
                      ? formatNum(summarySnapshot.balanceRatio, 2)
                      : '—'
                  }
                  status={tuningEval.summary.balance}
                  infoText={summaryTooltips.balance}
                />
                <SummaryPairedCapsule
                  label="Dynamic sag"
                  status={worstTuningStatus(tuningEval.summary.sagFront, tuningEval.summary.sagRear)}
                  lines={[
                    {
                      key: 'f',
                      sideClass: 'suspension-summary-pair-line--front',
                      tag: 'F',
                      primary:
                        summarySnapshot.dynSagFrontPct != null
                          ? `${formatNum(summarySnapshot.dynSagFrontPct, 1)}%`
                          : '—',
                    },
                    {
                      key: 'r',
                      sideClass: 'suspension-summary-pair-line--rear',
                      tag: 'R',
                      primary:
                        summarySnapshot.dynSagRearPct != null
                          ? `${formatNum(summarySnapshot.dynSagRearPct, 1)}%`
                          : '—',
                    },
                  ]}
                  infoText={summaryTooltips.dynSag}
                />
                <SummaryPairedCapsule
                  label="Travel use"
                  status={worstTuningStatus(tuningEval.summary.travelFront, tuningEval.summary.travelRear)}
                  lines={[
                    {
                      key: 'f',
                      sideClass: 'suspension-summary-pair-line--front',
                      tag: 'F',
                      primary:
                        summarySnapshot.travelFrontPct != null
                          ? `${formatNum(summarySnapshot.travelFrontPct, 1)}%`
                          : '—',
                    },
                    {
                      key: 'r',
                      sideClass: 'suspension-summary-pair-line--rear',
                      tag: 'R',
                      primary:
                        summarySnapshot.travelRearPct != null
                          ? `${formatNum(summarySnapshot.travelRearPct, 1)}%`
                          : '—',
                    },
                  ]}
                  infoText={summaryTooltips.travel}
                />
                <SummaryPairedCapsule
                  label="Bottom-outs"
                  status={worstTuningStatus(
                    tuningEval.summary.bottomFront,
                    tuningEval.summary.bottomRear,
                    tuningEval.summary.bottomTotal,
                  )}
                  lines={[
                    {
                      key: 'f',
                      sideClass: 'suspension-summary-pair-line--front',
                      tag: 'F',
                      primary: summarySnapshot.bottomF != null ? String(summarySnapshot.bottomF) : '—',
                    },
                    {
                      key: 'r',
                      sideClass: 'suspension-summary-pair-line--rear',
                      tag: 'R',
                      primary: summarySnapshot.bottomR != null ? String(summarySnapshot.bottomR) : '—',
                    },
                  ]}
                  infoText={summaryTooltips.bottom}
                />
                <SummaryPairedCapsule
                  label="Top-outs"
                  status={tuningEval.summary.topBalance}
                  lines={[
                    {
                      key: 'f',
                      sideClass: 'suspension-summary-pair-line--front',
                      tag: 'F',
                      primary: summarySnapshot.topF != null ? String(summarySnapshot.topF) : '—',
                    },
                    {
                      key: 'r',
                      sideClass: 'suspension-summary-pair-line--rear',
                      tag: 'R',
                      primary: summarySnapshot.topR != null ? String(summarySnapshot.topR) : '—',
                    },
                  ]}
                  infoText={summaryTooltips.top}
                />
                <SummaryMetricCapsule
                  label="Packing"
                  value={
                    summarySnapshot.jackOk && summarySnapshot.packingIndex != null
                      ? `${Math.round(Number(summarySnapshot.packingIndex))}%`
                      : '—'
                  }
                  valueSub={
                    summarySnapshot.jackOk && summarySnapshot.packingRating
                      ? String(summarySnapshot.packingRating)
                      : null
                  }
                  status={summarySnapshot.jackOk ? tuningEval.summary.packing : 'neutral'}
                  infoText={summaryTooltips.packing}
                />
                <SummaryMetricCapsule
                  label="Roughness"
                  value={formatNum(summarySnapshot.roughness, 2)}
                  status={tuningEval.summary.roughness}
                  infoText={summaryTooltips.roughness}
                />
              </div>

              <div className="suspension-kb-block">
                <h4 className="suspension-kb-block-title">Recommendations</h4>
                <SuspensionRecommendationsBlock kbSlices={tuningEval.kbSlices} />
              </div>
            </>
          )}
        </div>
      )}

      {effectiveTab === 'ai-insight' && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          <div className="suspension-openai-tab">
            <div className="suspension-openai-actions">
              <button type="button" className="suspension-openai-button" disabled={openAiLoading} onClick={handleOpenAiInsight}>
                <FaBrain aria-hidden />
                <span>{openAiLoading ? 'Requesting…' : 'Generate insight'}</span>
              </button>
            </div>
            {openAiError ? <p className="suspension-jack-error suspension-openai-error">{openAiError}</p> : null}
            {openAiText ? (
              <div className="suspension-openai-insight-body" dangerouslySetInnerHTML={{ __html: openAiHtml }} />
            ) : (
              <p className="suspension-jack-empty">Generate an insight to view the AI analysis.</p>
            )}
          </div>
        </div>
      )}

      {effectiveTab === 'statistics' && !jackResult.ok && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          <p className="suspension-jack-error">
            {jackResult.error || 'Could not run Jack analysis for statistics.'}
            {jackResult.sampleCount != null ? ` (${jackResult.sampleCount} samples after filter)` : ''}
          </p>
        </div>
      )}

      {effectiveTab === 'statistics' && jackResult.ok && !jackResult.analysisResults?.stats && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          <p className="suspension-jack-error">Jack analysis completed but no statistics object was returned.</p>
        </div>
      )}

      {effectiveTab === 'statistics' && jackResult.ok && jackResult.analysisResults?.stats && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          <div className="suspension-jack-benchmark-stats-pair">
            <div className="suspension-jack-benchmark-stats-column suspension-jack-benchmark-stats-column--front">
              <h3 className="suspension-jack-benchmark-stats-heading suspension-jack-benchmark-stats-heading--front">
                Front Suspension Statistics
              </h3>
              <JackBenchmarkStatsTable
                sideLabel="Front"
                stats={jackResult.analysisResults.stats.front}
                totalTravelMm={jackResult.analysisResults.params?.frontTravel}
                rowStatusMap={tuningEval.jackFrontRows}
              />
            </div>
            <div className="suspension-jack-benchmark-stats-column suspension-jack-benchmark-stats-column--rear">
              <h3 className="suspension-jack-benchmark-stats-heading suspension-jack-benchmark-stats-heading--rear">
                Rear Suspension Statistics
              </h3>
              <JackBenchmarkStatsTable
                sideLabel="Rear"
                stats={jackResult.analysisResults.stats.rear}
                totalTravelMm={jackResult.analysisResults.params?.rearTravel}
                rowStatusMap={tuningEval.jackRearRows}
              />
            </div>
          </div>
        </div>
      )}

      {effectiveTab === 'packing' && (
        <div className="suspension-panel-tab-content suspension-jack-section" role="tabpanel">
          {!jackResult.ok ? (
            <p className="suspension-jack-error">
              {jackResult.error || 'Could not run packing analysis.'}
              {jackResult.sampleCount != null ? ` (${jackResult.sampleCount} samples after filter)` : ''}
            </p>
          ) : (
            <>
              <p className="suspension-jack-graph-hint">
                Position and velocity vs time are on the <strong>Graph</strong> panel: two synced <strong>Plotly</strong> charts (same style as the Jack benchmark page).
              </p>
              <div className={`suspension-jack-rating suspension-jack-rating--${(jackResult.analysisResults.rating || '').toLowerCase()}`}>
                <div className="suspension-jack-score">{jackResult.analysisResults.packingIndex}</div>
                <div className="suspension-jack-rating-label">Packing index — {jackResult.analysisResults.rating}</div>
                <div className="suspension-jack-subscores">
                  <span className="suspension-jack-subscore-front">
                    Front {jackResult.analysisResults.frontPackingIndex}
                  </span>
                  {' · '}
                  <span className="suspension-jack-subscore-rear">
                    Rear {jackResult.analysisResults.rearPackingIndex}
                  </span>
                </div>
              </div>
              {jackSummary && (
                <div className="suspension-jack-summary-card">
                  <h3>Packing summary</h3>
                  <div className="suspension-jack-summary-grid">
                    <div className="suspension-jack-summary-metric">
                      <div className="suspension-jack-summary-value">{formatNum(jackSummary.packingRatio, 0)}</div>
                      <div className="suspension-jack-summary-label">Packing Ratio %</div>
                    </div>
                    <div className="suspension-jack-summary-metric">
                      <div className="suspension-jack-summary-value">{formatNum(jackSummary.avgSeverity, 0)}</div>
                      <div className="suspension-jack-summary-label">Avg Severity</div>
                    </div>
                    <div className="suspension-jack-summary-metric">
                      <div className="suspension-jack-summary-value">{formatNum(jackSummary.worstSeverity, 0)}</div>
                      <div className="suspension-jack-summary-label">Worst Event</div>
                    </div>
                    <div className="suspension-jack-summary-metric">
                      <div className="suspension-jack-summary-value">{formatNum(jackSummary.timePackedPercent, 0)}</div>
                      <div className="suspension-jack-summary-label">Time Packed %</div>
                    </div>
                    <div className="suspension-jack-summary-metric">
                      <div className="suspension-jack-summary-value">{formatNum(jackSummary.packingEvents, 0)}</div>
                      <div className="suspension-jack-summary-label">Packing Events</div>
                    </div>
                    <div className="suspension-jack-summary-metric">
                      <div className="suspension-jack-summary-value">{formatNum(jackSummary.totalImpacts, 0)}</div>
                      <div className="suspension-jack-summary-label">Total Impacts</div>
                    </div>
                  </div>
                </div>
              )}
              <p className="suspension-jack-hint">
                Same pipeline as Jack benchmark page (ADC → mm, Savitzky–Golay, impacts, packing events). Analysis runs when run data loads.
              </p>
            </>
          )}
        </div>
      )}

      {effectiveTab === 'suspension-traces' && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          <h4 className="suspension-tab-section-title">Suspension travel and velocity</h4>
          {jackResult.ok && displayProcessedData.length > 0 ? (
            <>
              <TimeSeriesChart
                series={displayProcessedData}
                getX={(p) => p.time}
                height={260}
                datasets={[
                  { label: 'Fork position', getY: (p) => p.front, borderColor: '#42a5f5', tension: 0.12, pointRadius: 0 },
                  { label: 'Shock position', getY: (p) => p.rear, borderColor: '#ec4899', tension: 0.12, pointRadius: 0 },
                ]}
                options={{
                  scales: {
                    x: { type: 'linear', title: { display: true, text: 'Time (s)', color: '#9aa0a6' }, ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.06)' } },
                    y: { title: { display: true, text: 'Position (mm)', color: '#9aa0a6' }, ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.06)' } },
                  },
                }}
              />
              <TimeSeriesChart
                series={displayProcessedData}
                getX={(p) => p.time}
                height={260}
                datasets={[
                  { label: 'Fork velocity', getY: (p) => p.front_vel, borderColor: '#64b5f6', tension: 0.12, pointRadius: 0 },
                  { label: 'Shock velocity', getY: (p) => p.rear_vel, borderColor: '#f472b6', tension: 0.12, pointRadius: 0 },
                ]}
                options={{
                  scales: {
                    x: { type: 'linear', title: { display: true, text: 'Time (s)', color: '#9aa0a6' }, ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.06)' } },
                    y: { title: { display: true, text: 'Velocity (mm/s)', color: '#9aa0a6' }, ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.06)' } },
                  },
                }}
              />
            </>
          ) : (
            <p className="suspension-jack-error">Jack analysis is required for suspension traces.</p>
          )}
        </div>
      )}

      {(effectiveTab === 'position-distrib' || effectiveTab === 'velocity-distrib' || effectiveTab === 'stroke-length') && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          {jackResult.ok && jackResult.analysisResults?.stats ? (
            <JackAdvancedHistograms
              jackOk={jackResult.ok}
              processedData={displayProcessedData}
              analysisResults={jackResult.analysisResults}
              view={effectiveTab === 'position-distrib' ? 'position' : effectiveTab === 'velocity-distrib' ? 'velocity' : 'stroke'}
            />
          ) : (
            <p className="suspension-jack-error">Jack analysis is required for distribution panels.</p>
          )}
        </div>
      )}

      {effectiveTab === 'frequency' && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          <FrequencyHistogramPanel series={frequencySeries} />
        </div>
      )}

      {effectiveTab === 'auto-tuning' && (
        <AutoTuningPanel
          tuningEval={tuningEval}
          summarySnapshot={summarySnapshot}
          analysisResults={jackResult.ok ? jackResult.analysisResults : null}
          tuningRanges={tuningRanges}
        />
      )}

      {effectiveTab === 'imu' && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          <h4 className="suspension-tab-section-title">Frame IMU (accelerometer Z)</h4>
          <TimeSeriesChart
            series={imuSeries}
            getX={(p) => p.time}
            height={260}
            datasets={[
              { label: 'Frame Accel Z', getY: (p) => p.frame_accel_z, borderColor: '#ffb74d', tension: 0.15, pointRadius: 0 },
              { label: 'Bar Accel Z', getY: (p) => p.bar_accel_z, borderColor: '#ab47bc', tension: 0.15, pointRadius: 0 },
            ]}
            options={{
              scales: {
                x: { type: 'linear', title: { display: true, text: 'Time (s)', color: '#9aa0a6' }, ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.06)' } },
                y: { title: { display: true, text: 'Acceleration (g)', color: '#9aa0a6' }, ticks: { color: '#888' }, grid: { color: 'rgba(255,255,255,0.06)' } },
              },
            }}
          />
        </div>
      )}

      {effectiveTab === 'events' && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          <h4 className="suspension-tab-section-title">Packing events</h4>
          {!jackResult.ok ? (
            <p className="suspension-jack-error">Run packing analysis to see events.</p>
          ) : jackEventsList.length === 0 ? (
            <p className="suspension-jack-empty">No packing events detected.</p>
          ) : (
            <div className="suspension-events-list">
              {jackEventsList.map((ev) => (
                <button
                  key={ev.key}
                  type="button"
                  className={`suspension-event-row suspension-event-row--jack ${
                    ev.side === 'Front' ? 'suspension-event-row--jack-front' : 'suspension-event-row--jack-rear'
                  }`}
                  onClick={() => {
                    if (onJackPackingEventSelect) {
                      onJackPackingEventSelect({
                        startTime: ev.start_time,
                        endTime: ev.end_time,
                      });
                    } else if (onSeek) {
                      onSeek(ev.start_time - (Number(dataOffset) || 0));
                    }
                  }}
                  title={
                    onJackPackingEventSelect || onSeek
                      ? 'Zoom graph to event (±1.5s) and seek — same as Jack benchmark'
                      : undefined
                  }
                >
                  <span className="suspension-event-type">{ev.side}</span>
                  <span className="suspension-event-start">{formatTime(ev.start_time)}</span>
                  <span className="suspension-event-finish">{formatTime(ev.end_time)}</span>
                  <span className="suspension-event-duration">{formatDuration(ev.duration)}</span>
                  <span className="suspension-jack-ev-meta">{ev.category || '—'} · hits {ev.hit_count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {effectiveTab === 'dynamic-balance' && (
        <div className="suspension-panel-tab-content byb-susp-split" role="tabpanel">
          {jackResult.ok && displayProcessedData.length > 0 ? (
            <>
              <BybScatterFrame title="Fork dynamic balance" xLabel="Suspension stroke length (%)" yLabel="Suspension velocity (mm/s)">
                <BybScatterPlot
                  rows={displayProcessedData}
                  xGetter={(p) => (p.front / (Number(jackResult.analysisResults?.params?.frontTravel) || 200)) * 100}
                  yGetter={(p) => p.front_vel}
                  color="#42a5f5"
                  xDomain={{ min: 0, max: 100 }}
                />
              </BybScatterFrame>
              <BybScatterFrame title="Shock dynamic balance" xLabel="Suspension position (%)" yLabel="Suspension velocity (mm/s)">
                <BybScatterPlot
                  rows={displayProcessedData}
                  xGetter={(p) => (p.rear / (Number(jackResult.analysisResults?.params?.rearTravel) || 200)) * 100}
                  yGetter={(p) => p.rear_vel}
                  color="#ef4444"
                  xDomain={{ min: 0, max: 100 }}
                />
              </BybScatterFrame>
            </>
          ) : (
            <p className="suspension-jack-error">Jack analysis is required for dynamic balance.</p>
          )}
        </div>
      )}

      {effectiveTab === 'position-velocity' && (
        <div className="suspension-panel-tab-content byb-susp-split" role="tabpanel">
          {jackResult.ok && displayProcessedData.length > 0 ? (
            <>
              <BybScatterFrame title="Fork position vs velocity" xLabel="Suspension position %" yLabel="Suspension velocity (mm/s)">
                <BybScatterPlot
                  rows={displayProcessedData}
                  xGetter={(p) => (p.front / (Number(jackResult.analysisResults?.params?.frontTravel) || 200)) * 100}
                  yGetter={(p) => p.front_vel}
                  color="#42a5f5"
                  xDomain={{ min: 0, max: 100 }}
                  lines
                />
              </BybScatterFrame>
              <BybScatterFrame title="Shock position vs velocity" xLabel="Suspension position %" yLabel="Suspension velocity (mm/s)">
                <BybScatterPlot
                  rows={displayProcessedData}
                  xGetter={(p) => (p.rear / (Number(jackResult.analysisResults?.params?.rearTravel) || 200)) * 100}
                  yGetter={(p) => p.rear_vel}
                  color="#ef4444"
                  xDomain={{ min: 0, max: 100 }}
                  lines
                />
              </BybScatterFrame>
            </>
          ) : (
            <p className="suspension-jack-error">Jack analysis is required for position vs velocity.</p>
          )}
        </div>
      )}

      {effectiveTab === 'balance' && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          {!jackResult.ok ? (
            <p className="suspension-jack-error">
              {jackResult.error || 'Run Jack analysis to see stroke depth vs speed balance (same engine as packing analyzer).'}
              {jackResult.sampleCount != null ? ` (${jackResult.sampleCount} samples after filter)` : ''}
            </p>
          ) : !jackResult.analysisResults?.stats ? (
            <p className="suspension-jack-error">Jack statistics are required for the balance chart.</p>
          ) : (
            <div className="suspension-velocity-balance">
              <div className="suspension-velocity-balance-title">Stroke depth vs speed balance</div>
              <JackStrokeBalanceChart analysisResults={jackResult.analysisResults} />
              <p className="suspension-velocity-balance-hint">
                Each point is one stroke. Depth (horizontal, mm) vs max speed in that stroke (vertical, mm/s). Blue:
                front, red: rear. Dashed lines are linear trends; slope shows how much velocity grows with depth. Use
                stroke and speed filters to separate compression, rebound, and low vs high speed.
              </p>
            </div>
          )}
        </div>
      )}

      {effectiveTab === 'advanced' && (
        <div className="suspension-panel-tab-content" role="tabpanel">
          {jackResult.ok && jackResult.analysisResults?.stats ? (
            <JackAdvancedHistograms
              jackOk={jackResult.ok}
              processedData={displayProcessedData}
              analysisResults={jackResult.analysisResults}
            />
          ) : (
            <p className="suspension-jack-error">
              Jack analysis is required for axle position and stroke histograms (same engine as Jack benchmark).
            </p>
          )}

          {metrics ? (
            <>
              <h4 className="suspension-tab-section-title suspension-tab-section-title--spaced">
                Travel % time by decile (smoothed telemetry)
              </h4>
              <div className="suspension-histograms">
                <div className="suspension-hist-block">
                  <div className="suspension-hist-title">Travel histogram (front) % time in bin</div>
                  <div className="suspension-hist-bars">
                    {(metrics.travel_histogram_front || []).map((pct, idx) => (
                      <div key={idx} className="suspension-hist-row">
                        <span className="suspension-hist-label">{histLabels[idx]}</span>
                        <div className="suspension-hist-bar-wrap">
                          <div className="suspension-hist-bar" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <span className="suspension-hist-pct">{formatNum(pct, 1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="suspension-hist-block">
                  <div className="suspension-hist-title">Travel histogram (rear) % time in bin</div>
                  <div className="suspension-hist-bars">
                    {(metrics.travel_histogram_rear || []).map((pct, idx) => (
                      <div key={idx} className="suspension-hist-row">
                        <span className="suspension-hist-label">{histLabels[idx]}</span>
                        <div className="suspension-hist-bar-wrap">
                          <div className="suspension-hist-bar rear" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <span className="suspension-hist-pct">{formatNum(pct, 1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : !jackResult.ok ? (
            <p className="suspension-jack-error">Not enough travel data for decile histograms.</p>
          ) : null}
        </div>
      )}
    </div>
  );
}
